import { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import { Bell, Repeat, Check } from "lucide-react";
import type { TaskLink } from "@shared/taskLinks";
import { TaskLinkChips } from "./TaskLinkChips";

export interface TaskRow {
  id: string;
  title: string;
  description?: string | null;
  userId: string;
  createdById?: string | null;
  status: "pending" | "in_progress" | "completed" | "cancelled";
  priority: "low" | "medium" | "high" | "urgent";
  dueDate?: string | null;
  remindAt?: string | null;
  reminderFiredAt?: string | null;
  recurrenceRule?: string | null;
  assigneeName?: string | null;
  entityName?: string | null;
  entityKind?: "company" | "contact" | "deal" | null;
  entityId?: string | null;
  companyCategory?: string | null;
  /** Every record the task links to, in display order (server/taskRows.ts). */
  links: TaskLink[];
}

const PRIORITY_DOT: Record<string, string> = {
  urgent: "bg-red-500",
  high: "bg-amber-500",
  medium: "",
  low: "",
};

/**
 * Groups are the list's structure, and they encode the only thing that matters
 * in a to-do list: what is late, what is now, what can wait.
 */
const GROUP_ORDER = ["Overdue", "Today", "Tomorrow", "This week", "Later", "No date"] as const;
type Group = (typeof GROUP_ORDER)[number];

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

function groupFor(row: TaskRow): Group {
  if (!row.dueDate) return "No date";
  const due = new Date(row.dueDate);
  if (Number.isNaN(due.getTime())) return "No date";
  const today = startOfDay(new Date());
  const d = startOfDay(due);
  const day = 86_400_000;
  if (d < today) return "Overdue";
  if (d === today) return "Today";
  if (d === today + day) return "Tomorrow";
  if (d <= today + 7 * day) return "This week";
  return "Later";
}

/** "Fri 2pm" — the time only when there is one worth showing. */
function whenLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const today = startOfDay(new Date());
  const same = startOfDay(d) === today;
  const time = d.getHours() || d.getMinutes()
    ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: d.getMinutes() ? "2-digit" : undefined })
    : "";
  if (same) return time ? `Today ${time}` : "Today";
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return time ? `${date} ${time}` : date;
}

function Row({ row, onOpen }: { row: TaskRow; onOpen: (id: string) => void }) {
  const qc = useQueryClient();
  const done = row.status === "completed";

  const complete = useMutation({
    mutationFn: async () =>
      (await apiRequest("PATCH", `/api/tasks/${row.id}`, {
        status: done ? "pending" : "completed",
      })).json(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/tasks"] });
      qc.invalidateQueries({ queryKey: ["/api/reminders"] });
    },
  });

  const repeats = Boolean(row.recurrenceRule && row.recurrenceRule !== "none");
  const meta = [row.assigneeName, repeats ? row.recurrenceRule : null].filter(Boolean);

  return (
    <div
      className={cn(
        "group flex items-start gap-3 border-b border-border/60 px-4 py-2.5 transition-colors last:border-b-0 hover:bg-muted/40",
        done && "opacity-50",
      )}
    >
      <button
        onClick={() => complete.mutate()}
        aria-label={done ? "Mark not done" : "Mark done"}
        className={cn(
          "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
          done
            ? "border-accent bg-accent text-accent-foreground"
            : "border-muted-foreground/40 hover:border-accent",
        )}
      >
        {done && <Check className="h-3 w-3" />}
      </button>

      <div className="min-w-0 flex-1">
        <button onClick={() => onOpen(row.id)} className="block w-full min-w-0 text-left">
          <div className={cn("flex items-center gap-2 text-sm", done && "line-through")}>
            {PRIORITY_DOT[row.priority] && (
              <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", PRIORITY_DOT[row.priority])} />
            )}
            <span className="truncate">{row.title}</span>
          </div>
        </button>
        {/* The chips sit outside the button: a chip opens its record, the rest of the row opens the panel. */}
        {(row.links.length > 0 || meta.length > 0) && (
          <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
            <TaskLinkChips links={row.links} max={2} className="min-w-0 flex-nowrap" />
            {meta.length > 0 && (
              <button
                onClick={() => onOpen(row.id)}
                className="flex min-w-0 items-center gap-1.5 text-left"
              >
                {repeats && <Repeat className="h-3 w-3 shrink-0" />}
                <span className="truncate">{meta.join(" · ")}</span>
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1.5 self-center">
        {row.remindAt && <Bell className="h-3.5 w-3.5 text-primary-text" />}
        {row.dueDate && (
          <span className="text-xs tabular-nums text-muted-foreground">
            {whenLabel(row.dueDate)}
          </span>
        )}
      </div>
    </div>
  );
}

export function TaskList({
  rows,
  onOpen,
  emptyTitle,
  emptyBody,
}: {
  rows: TaskRow[];
  onOpen: (id: string) => void;
  emptyTitle: string;
  emptyBody: string;
}) {
  const groups = useMemo(() => {
    const map = new Map<Group, TaskRow[]>();
    for (const r of rows) {
      const g = groupFor(r);
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(r);
    }
    // Within a group, soonest first; undated last.
    for (const list of map.values()) {
      list.sort((a, b) => {
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      });
    }
    return GROUP_ORDER.filter((g) => map.has(g)).map((g) => ({ label: g, rows: map.get(g)! }));
  }, [rows]);

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card px-4 py-12 text-center">
        <p className="text-sm font-medium">{emptyTitle}</p>
        <p className="mt-1 text-sm text-muted-foreground">{emptyBody}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <div key={g.label} className="overflow-hidden rounded-lg border border-border bg-card">
          <div
            className={cn(
              "flex items-baseline justify-between border-b border-border/60 px-4 py-1.5",
              // Overdue is the one thing on this page allowed to shout.
              g.label === "Overdue" ? "bg-red-500/10" : "bg-muted/50",
            )}
          >
            <span
              className={cn(
                "text-[11px] font-semibold uppercase tracking-[0.09em]",
                g.label === "Overdue" ? "text-red-600 dark:text-red-400" : "text-muted-foreground",
              )}
            >
              {g.label}
            </span>
            <span className="text-xs tabular-nums text-muted-foreground">{g.rows.length}</span>
          </div>
          {g.rows.map((r) => (
            <Row key={r.id} row={r} onOpen={onOpen} />
          ))}
        </div>
      ))}
    </div>
  );
}
